// TimesheetEditModal.js - Fixed to send all changes in one request
import React, { useState, useEffect } from 'react';
import { apiPut } from '../../utils/apiFetch';
import pathObj from 'utils/pathObj';
import useToastContext from 'hooks/useToastContext';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import ErrorMessage from 'components/ErrorMessage';

const TimesheetEditModal = ({ show, onHide, timesheet, dayIndex, refreshList }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [selectedDay, setSelectedDay] = useState(dayIndex || 0);
  const [formData, setFormData] = useState({
    punchInDateTime: '',
    punchOutDateTime: '',
    attendanceStatus: 'none',
    reason: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal is closed
  useEffect(() => {
    if (!show) {
      setFormData({
        punchInDateTime: '',
        punchOutDateTime: '',
        attendanceStatus: 'none',
        reason: ''
      });
      setErrors({});
      setSelectedDay(dayIndex || 0);
    }
  }, [show, dayIndex]);

  // Load data when modal opens or day changes
  useEffect(() => {
    if (show && timesheet?.dailyEntries) {
      const entry = timesheet.dailyEntries[selectedDay];
      
      // Calculate the date for the selected day
      const weekStart = dayjs(timesheet.weekStartDate);
      const entryDate = weekStart.add(selectedDay, 'day');
      
      // Determine attendance status
      let attendanceStatus = 'none';
      if (entry?.isAbsent) attendanceStatus = 'absent';
      else if (entry?.isLate) attendanceStatus = 'late';
      else if (entry?.isEarly) attendanceStatus = 'early';
      
      // If entry exists with punch data, use its data
      if (entry && entry.punchInDateTime) {
        setFormData({
          punchInDateTime: entry.punchInDateTime, // Use as-is, no conversion
          punchOutDateTime: entry.punchOutDateTime || '',
          attendanceStatus: attendanceStatus,
          reason: ''
        });
      } else {
        // For days with no data, pre-fill the date with default time
        const dateStr = entryDate.format('YYYY-MM-DD');
        setFormData({
          punchInDateTime: dateStr + 'T09:00',
          punchOutDateTime: dateStr + 'T18:00',
          attendanceStatus: 'none',
          reason: ''
        });
      }
      setErrors({});
    }
  }, [timesheet, selectedDay, show]);

  const validateForm = () => {
    let newErrors = {};
    if (!formData.reason || formData.reason.trim() === '') {
      newErrors.reason = t('PLEASE_ENTER_REASON_FOR_EDIT');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      notification.error(t('PLEASE_FIX_ERRORS'));
      return;
    }

    try {
      setIsLoading(true);
      
      const baseUrl = `${pathObj.editTimesheet}/${timesheet._id}/edit/${selectedDay}`;
      const currentEntry = timesheet.dailyEntries[selectedDay];
      const timezone = timesheet.timezone || 'America/New_York';

      // ─── BUILD A SINGLE UPDATE WITH ALL CHANGES ──────────────────────
      const updates = [];
      
      // Attendance status changes
      const newIsAbsent = formData.attendanceStatus === 'absent';
      const newIsLate = formData.attendanceStatus === 'late';
      const newIsEarly = formData.attendanceStatus === 'early';

      if (newIsAbsent !== (currentEntry?.isAbsent || false)) {
        updates.push({
          field: 'isAbsent',
          value: newIsAbsent
        });
      }

      if (newIsLate !== (currentEntry?.isLate || false)) {
        updates.push({
          field: 'isLate',
          value: newIsLate
        });
      }

      if (newIsEarly !== (currentEntry?.isEarly || false)) {
        updates.push({
          field: 'isEarly',
          value: newIsEarly
        });
      }

      // Punch time changes
      if (formData.punchInDateTime && formData.punchInDateTime.length > 0) {
        const newPunchIn = formData.punchInDateTime;
        const oldPunchIn = currentEntry?.punchInDateTime || '';
        
        if (newPunchIn !== oldPunchIn) {
          updates.push({
            field: 'punchInDateTime',
            value: newPunchIn
          });
        }
      }

      if (formData.punchOutDateTime && formData.punchOutDateTime.length > 0) {
        const newPunchOut = formData.punchOutDateTime;
        const oldPunchOut = currentEntry?.punchOutDateTime || '';
        
        if (newPunchOut !== oldPunchOut) {
          updates.push({
            field: 'punchOutDateTime',
            value: newPunchOut
          });
        }
      }

      if (updates.length === 0) {
        notification.error(t('NO_CHANGES_TO_SAVE'));
        return;
      }

      // ─── SEND ALL UPDATES IN A SINGLE REQUEST ────────────────────────
      // Instead of multiple API calls, send all updates in one request
      const payload = {
        updates: updates,
        reason: formData.reason
      };

      await apiPut(baseUrl, payload);
      
      notification.success(t('TIMESHEET_UPDATED_SUCCESSFULLY'));
      refreshList();
      onHide();
    } catch (error) {
      console.error('Error updating timesheet:', error);
      notification.error(t('SOMETHING_WENT_WRONG'));
    } finally {
      setIsLoading(false);
    }
  };

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const hasExistingData = timesheet?.dailyEntries?.[selectedDay] && 
                          (timesheet.dailyEntries[selectedDay].punchInDateTime || 
                           timesheet.dailyEntries[selectedDay].isLate || 
                           timesheet.dailyEntries[selectedDay].isEarly || 
                           timesheet.dailyEntries[selectedDay].isAbsent);

  if (!show || !timesheet) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black opacity-40" onClick={onHide} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden outline-none focus:outline-none pointer-events-none">
        <div className="relative w-full my-6 mx-auto max-w-2xl pointer-events-auto">
          <div className="border-0 rounded-xl shadow-xl relative flex flex-col w-full bg-white outline-none max-h-[90vh]">
            
            <div className="p-4 flex items-center justify-between border-b border-solid border-slate-100 rounded-t">
              <h3 className="font-bold text-lg text-gray-800">{t('EDIT_TIMESHEET')}</h3>
              <button onClick={onHide} className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600">
                <span className="text-2xl font-light">×</span>
              </button>
            </div>

            <div className="relative p-5 flex-auto max-h-[70vh] overflow-y-auto">
              <div className="relative z-0 w-full group mb-4">
                <select
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(Number(e.target.value))}
                  className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none focus:outline-none focus:ring-0 peer"
                >
                  {daysOfWeek.map((day, idx) => {
                    const entry = timesheet?.dailyEntries?.[idx];
                    const weekStart = dayjs(timesheet.weekStartDate);
                    const entryDate = weekStart.add(idx, 'day');
                    return (
                      <option key={idx} value={idx}>
                        {day} - {entry ? dayjs(entry.date).format('MMM D') : entryDate.format('MMM D')}
                      </option>
                    );
                  })}
                </select>
                <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                  {t('SELECT_DAY')}
                </label>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg mb-4 border border-gray-100">
                {hasExistingData ? (
                  <>
                    <small className="text-gray-500 block mb-2">{t('CURRENT_VALUES')}:</small>
                    <div className="flex gap-4">
                      <div>
                        <small className="text-gray-500">{t('PUNCH_IN')}:</small>
                        <br />
                        <strong>
                          {timesheet.dailyEntries[selectedDay].punchInDateTime
                            ? dayjs(timesheet.dailyEntries[selectedDay].punchInDateTime).format('hh:mm A')
                            : 'N/A'}
                        </strong>
                      </div>
                      <div>
                        <small className="text-gray-500">{t('PUNCH_OUT')}:</small>
                        <br />
                        <strong>
                          {timesheet.dailyEntries[selectedDay].punchOutDateTime
                            ? dayjs(timesheet.dailyEntries[selectedDay].punchOutDateTime).format('hh:mm A')
                            : 'N/A'}
                        </strong>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-2">
                    <span className="text-gray-500 text-sm font-medium">
                      {t('NO_EXISTING_DATA_FOR_THIS_DAY')}
                    </span>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('ATTENDANCE_STATUS')}</label>
                <div className="grid grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="statusNone"
                      name="attendanceStatus"
                      value="none"
                      checked={formData.attendanceStatus === 'none'}
                      onChange={(e) => setFormData({ ...formData, attendanceStatus: e.target.value })}
                      className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                    />
                    <label htmlFor="statusNone" className="text-sm text-gray-700">{t('NONE')}</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="statusLate"
                      name="attendanceStatus"
                      value="late"
                      checked={formData.attendanceStatus === 'late'}
                      onChange={(e) => setFormData({ ...formData, attendanceStatus: e.target.value })}
                      className="w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-500"
                    />
                    <label htmlFor="statusLate" className="text-sm text-gray-700">{t('LATE')}</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="statusEarly"
                      name="attendanceStatus"
                      value="early"
                      checked={formData.attendanceStatus === 'early'}
                      onChange={(e) => setFormData({ ...formData, attendanceStatus: e.target.value })}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <label htmlFor="statusEarly" className="text-sm text-gray-700">{t('EARLY')}</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="statusAbsent"
                      name="attendanceStatus"
                      value="absent"
                      checked={formData.attendanceStatus === 'absent'}
                      onChange={(e) => setFormData({ ...formData, attendanceStatus: e.target.value })}
                      className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                    />
                    <label htmlFor="statusAbsent" className="text-sm text-gray-700">{t('ABSENT')}</label>
                  </div>
                </div>
              </div>

              <div className="relative z-0 w-full group mb-4">
                <input
                  type="datetime-local"
                  value={formData.punchInDateTime}
                  onChange={(e) => setFormData({ ...formData, punchInDateTime: e.target.value })}
                  className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 peer"
                />
                <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                  {t('NEW_PUNCH_IN_TIME')}
                </label>
              </div>

              <div className="relative z-0 w-full group mb-4">
                <input
                  type="datetime-local"
                  value={formData.punchOutDateTime}
                  onChange={(e) => setFormData({ ...formData, punchOutDateTime: e.target.value })}
                  className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 peer"
                />
                <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                  {t('NEW_PUNCH_OUT_TIME')}
                </label>
              </div>

              <div className="relative z-0 w-full group">
                <textarea
                  rows={3}
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder={t('ENTER_REASON')}
                  className="block py-4 px-4 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 peer resize-none"
                />
                <label className="duration-300 absolute text-xs text-[#A5A5A5] transform -translate-y-5 scale-90 top-3 left-2 bg-white px-1 z-10">
                  {t('REASON_FOR_EDIT')} <span className="text-red-500">*</span>
                </label>
                <ErrorMessage message={errors?.reason} />
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 p-5 border-t border-solid border-slate-100 rounded-b">
              <button onClick={onHide} className="min-w-[120px] text-gray-700 bg-gray-200 font-medium px-6 py-2 text-sm rounded-md hover:bg-gray-300">
                {t('CANCEL')}
              </button>
              {isLoading ? (
                <div className="min-w-[120px] flex justify-center bg-[#4F46E5] px-6 py-2 rounded-md">
                  <div className="loading-spinner h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                </div>
              ) : (
                <button onClick={handleSave} className="min-w-[120px] bg-[#4F46E5] text-white font-medium px-6 py-2 text-sm rounded-md hover:bg-[#4338CA]">
                  {t('SAVE_CHANGES')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TimesheetEditModal;